import { ArrowUpDown, X, Check, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { WorkflowSheet } from '@/types/workflow';

interface Props {
  active: boolean;
  sheets: WorkflowSheet[];
  activeSheetIndex: number;
  source: { sheetIndex: number; blockId: string; blockName: string } | null;
  onCancel: () => void;
  onSwitchSheet: (index: number) => void;
}

/**
 * Enterprise-grade banner shown while cross-sheet linking mode is active.
 * Appears fixed at the top of the viewport, above the canvas. It coaches the
 * user through the two-step gesture (pick source → switch sheet → pick target)
 * and exposes one-tap sheet switchers for the adjacent sheets.
 *
 * Design goals:
 *   - Always visible and readable (backdrop-blur, enterprise navy accent).
 *   - Stateful: source-picked state shows the block name and highlights
 *     which sheets are valid next clicks.
 *   - Keyboard: Esc cancels (handled by the parent effect in Index.tsx).
 *   - Never blocks canvas interaction — `pointer-events-none` on the outer,
 *     `pointer-events-auto` on the card itself.
 */
export function CrossSheetLinkBanner({
  active, sheets, activeSheetIndex, source, onCancel, onSwitchSheet,
}: Props) {
  if (!active) return null;

  const sourceSheet = source ? sheets[source.sheetIndex] : null;
  const currentSheet = sheets[activeSheetIndex];

  const adjacentIndices: number[] = source
    ? [source.sheetIndex - 1, source.sheetIndex + 1].filter(i => i >= 0 && i < sheets.length)
    : [];

  return (
    <div
      className="fixed top-0 left-0 right-0 z-40 flex justify-center pt-3 px-4 pointer-events-none"
      role="status"
      aria-live="polite"
    >
      <div
        className="pointer-events-auto max-w-3xl w-full flex items-center gap-3 px-4 py-2.5 rounded-xl shadow-xl border backdrop-blur-md"
        style={{
          backgroundColor: 'rgba(26, 46, 90, 0.92)',
          borderColor: 'rgba(255,255,255,0.12)',
          color: 'white',
        }}
      >
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center">
            <ArrowUpDown className="w-4 h-4" aria-hidden="true" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-[11px] uppercase tracking-wider opacity-70">
              Cross-sheet link
            </span>
            <span className="text-sm font-semibold">
              {source ? 'Pick target on adjacent sheet' : 'Pick a source block'}
            </span>
          </div>
        </div>

        {/* Step indicator */}
        <div className="hidden md:flex items-center gap-1.5 text-[11px] opacity-80 flex-1 justify-center">
          <span className={source ? 'opacity-50' : 'font-medium'}>
            1. Click source {!source && <span className="ml-0.5">◉</span>}
          </span>
          <ChevronRight className="w-3 h-3" aria-hidden="true" />
          <span className={source ? 'font-medium' : 'opacity-50'}>
            2. Switch sheet {source && <span className="ml-0.5">◉</span>}
          </span>
          <ChevronRight className="w-3 h-3" aria-hidden="true" />
          <span className="opacity-50">3. Click target</span>
        </div>

        {/* Source block info + quick-switch chips */}
        {source && (
          <div className="flex items-center gap-2 flex-shrink-0 ml-auto md:ml-0">
            <div className="hidden sm:flex items-center gap-1.5 text-xs bg-white/10 rounded-md px-2 py-1">
              <Check className="w-3 h-3 text-green-300" aria-hidden="true" />
              <span className="max-w-[100px] truncate font-medium">{source.blockName}</span>
              <span className="opacity-50">on {sourceSheet?.name ?? `Sheet ${source.sheetIndex + 1}`}</span>
            </div>
            {adjacentIndices.map(i => (
              <Button
                key={i}
                variant="ghost"
                size="sm"
                onClick={() => onSwitchSheet(i)}
                className={
                  'h-7 px-2 text-xs rounded-md bg-white/10 hover:bg-white/20 text-white border ' +
                  (activeSheetIndex === i
                    ? 'border-white/70 shadow-md'
                    : 'border-transparent animate-pulse')
                }
              >
                {i < source.sheetIndex ? '↑ ' : '↓ '}
                {sheets[i]?.name ?? `Sheet ${i + 1}`}
              </Button>
            ))}
          </div>
        )}

        {/* Current sheet hint when source not set yet */}
        {!source && currentSheet && (
          <div className="hidden sm:flex items-center gap-1.5 text-xs opacity-70 ml-auto">
            <span>on</span>
            <span className="font-medium">{currentSheet.name}</span>
          </div>
        )}

        <Button
          variant="ghost"
          size="icon"
          onClick={onCancel}
          className="h-7 w-7 rounded-md text-white hover:bg-white/15 flex-shrink-0"
          aria-label="Cancel cross-sheet link"
          title="Cancel (Esc)"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
